import type { ComponentProps } from 'svelte'
import type Svelecte from 'svelecte'

/** Methods exposed on svelecte via `bind:this` (see `svelecte/dist/Svelecte.svelte.d.ts`). */
export type SvelecteControlApi = {
	focus: () => void
	setSelection: (selection: unknown, triggerChangeEvent: unknown) => void
	getSelection: (onlyValues: unknown) => unknown
	refetchWith: (value: string | number | unknown[]) => void
}

export type SvelecteFieldComponentProps = ComponentProps<typeof Svelecte>

/**
 * Per-instance svelecte i18n overrides. Defined locally because svelecte v5's
 * exports map does not expose the internal `svelecte/dist/settings` path.
 * Matches I18nObject from svelecte's settings.d.ts (subset — extend as needed).
 */
type SvelecteI18n = Partial<{
	empty: string
	nomatch: string
	fetchBefore: string
	fetchEmpty: string
	fetchInit: string
	emptyCreatable: string
	max: (max: number) => string
	fetchQuery: (minQuery: number, inputLength: number) => string
	collapsedSelection: (count: number) => string
	createRowLabel: (value: string) => string
}>

/**
 * Svelecte SearchProps type — defined locally because svelecte v5's exports map
 * does not expose the internal `svelecte/dist/utils/list` path.
 * Matches the SearchProps from svelecte's list.d.ts.
 */
export type SearchProps = {
	fields?: string | string[]
	sort?: string | Array<{ field: string; direction?: 'asc' | 'desc' }>
	keepSelectionInList?: boolean
	nesting?: boolean
	conjunction?: 'or'
	skipSort?: boolean
	startOnly?: boolean
	wordsOnly?: boolean
	disabled?: boolean
}

export type AutocompleteFieldConfig = {
	/*https://mskocik.github.io/svelecte*/
	clearable: boolean // shows an x icon at the end to clear all selections
	multiple: boolean // if multiple values can be selected
	disabled: boolean
	/** Static list or grouped structure; matches svelecte `options`. */
	options?: object[] | object
	fetch?: string | null // the url to fetch the options from
	fetchMode?: 'init' | 'auto'
	fetchCallback?: (fetched: unknown) => unknown
	groupLabelField?: string // when using grouped options, this is the header field in the object
	groupItemsField?: string // when using grouped options, this is the list of options in the object
	selectOnTab: boolean // select currently active item on TAB
	resetOnBlur: boolean // when focussing out of the input field, the text is deleted
	valueField?: string | null
	labelField?: string | null
	creatable?: boolean
	allowEditing: boolean
	searchable?: boolean
	disableHighlight?: boolean
	fetchResetOnBlur?: boolean
	creatablePrefix?: string
	valueAsObject?: boolean
	delimiter?: string
	minQuery?: number // minimum characters to trigger autocomplete search
	searchProps?: Partial<SearchProps>
	strictMode?: boolean // when this is set to false, svelecte does not check if the selection is in the list
	/** Per-instance i18n overrides — merged on top of svelecte's global config.i18n. */
	i18n?: SvelecteI18n
	/** Extra class applied to svelecte's `.sv_dropdown` panel for scoped styling. */
	dropdownClass?: string
}

export function DefaultAutocompleteConfig() {
	const config: AutocompleteFieldConfig = {
		clearable: true,
		disabled: false,
		multiple: false,
		selectOnTab: true,
		resetOnBlur: false,
		allowEditing: true
	}
	return config
}

export function AutocompleteConfig(
	valueField: string | null = null,
	labelField: string | null = null,
	multiple: boolean = false,
	disabled: boolean = false,
	selectOnTab: boolean = true,
	clearable: boolean = true,
	resetOnBlur: boolean = true,
	options?: object[] | object,
	fetch?: string
) {
	const config: AutocompleteFieldConfig = {
		clearable: clearable,
		disabled: disabled,
		multiple: multiple,
		selectOnTab: selectOnTab,
		resetOnBlur: resetOnBlur,
		valueField: valueField,
		labelField: labelField,
		options: options,
		fetch: fetch,
		allowEditing: true,
		// There is a bug that does not show spaces in specific cases, so its better we disable the highlighting in general for now
		disableHighlight: true,
		// Disable further filtering from Svelecte, since this is done in the BE already, this can lead to very strange or missing results
		searchProps: { skipSort: true }
	}
	return config
}

export function getFormAcConfigFetch(
	fetchKey: string,
	multiple = false,
	valueField = 'value',
	labelField = 'text',
	disabled: boolean = false,
	fetchResetOnBlur: boolean = true,
	fetchSuffix: string = ''
) {
	const config = AutocompleteConfig(valueField, labelField, multiple, disabled)
	config.fetch = '/api/' + fetchKey + '/autocomplete/svelte?qry=[query]' + fetchSuffix
	config.fetchResetOnBlur = fetchResetOnBlur
	return config
}
